import { Flex } from "antd";
import { useEffect, useMemo, useState } from "react";
import {
    getApiError,
    getPointInfoCollection,
    getRouteInfo,
    getSchedulerPointScheduleDetails,
} from "tm-shared/api";
import { arrayToRecord } from "tm-shared/utils";

import { useAppSelector, useProtectedUserRoleSelector } from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { BlockList } from "@/shared/pageComposition";
import { PrimaryText } from "@/shared/ui/Typography";

import { getMyRouteBlocks } from "./MyRoute.blocks";
import { buildMyRouteModel } from "./MyRoute.model";
import { MyRouteData } from "./MyRoute.types";
import { MyRoutePageSkeleton } from "./MyRoutePage.skeleton";

const MyRoutePage = () => {
    const route = useAppSelector((s) => s.user.route);
    const role = useProtectedUserRoleSelector();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState<MyRouteData | null>(null);

    useEffect(() => {
        const getRouteDetails = async () => {
            if (!route?.uuid) return;

            try {
                setLoading(true);
                setError("");
                setData(null);

                const { result: routeDetails } = await getRouteInfo({
                    id: route.uuid,
                });

                const { result: schedules } =
                    await getSchedulerPointScheduleDetails({
                        routeId: route.uuid,
                    });

                const pointIds = schedules.map(({ pointId }) => pointId);

                const { result: points } = await getPointInfoCollection({
                    ids: pointIds,
                });

                setData({
                    points: arrayToRecord(points, "id"),
                    routeDetails,
                    schedules,
                });
            } catch (fetchError) {
                const { message } = getApiError(fetchError);
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        void getRouteDetails();
    }, [route?.uuid]);

    const model = useMemo(() => {
        if (!data) return null;

        return buildMyRouteModel({
            role,
            routeName: route?.name,
            routeDetails: data.routeDetails,
            points: data.points,
            schedules: data.schedules,
        });
    }, [data, role, route?.name]);

    const blocks = useMemo(
        () => (model ? getMyRouteBlocks(model) : []),
        [model],
    );

    const renderContent = () => {
        if (loading) {
            return <MyRoutePageSkeleton />;
        }

        if (error) {
            return (
                <Flex vertical gap={16}>
                    <PrimaryText>Помилка при завантаженні даних</PrimaryText>
                    <PrimaryText textColor="error">{error}</PrimaryText>
                </Flex>
            );
        }

        if (!model) {
            return <div>Дані маршруту не знайдено</div>;
        }

        return (
            <Flex vertical gap={16}>
                <BlockList blocks={blocks} model={model} />
            </Flex>
        );
    };

    return (
        <>
            <Header title={model?.route?.title ?? route?.name} />

            <MainContainer>{renderContent()}</MainContainer>
        </>
    );
};

MyRoutePage.displayName = "MyRoutePage";

export default MyRoutePage;
